import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import Footer from './Footer';

const BlogPost = () => {
  const { id } = useParams();

  const blogPosts = {
    'panda-story': {
      title: 'The Story Behind Panda',
      date: 'January 2025',
      category: 'Story',
      readTime: '5 min read',
      imageUrl: '/panda-story.png',
      content: `🚀 Panda got 26K users in 15 days.

Wanted to share the story behind it, and the people who helped along the way.

People often think I built it solo, but truth is, I didn't.
Many helped in ways that mattered more than code.

A senior once told me:
"Build something so ambitious that smart people feel compelled to help you make it real."
That line stuck.

So, in my spare time, I started tinkering with a simple open-source side project, a voice assistant that could someday go beyond Siri or Google Assistant.
Something that could use any app, listen, and talk back like a real assistant.

Went through 30–40 GitHub repos and research papers for a week,
Built the 1st version in 10 days, it wished my brother happy b'day… in 5 mins 😅

Then came a 48-hr hackathon.
Brought that 5-min wish time down to 2.

Didn't win, but a judge DM'd me: "Super awesome project."
That meant more than a trophy.
Still grateful to Ariel Weinberger for organizing it, Panda needed that push.

After the hackathon, I kept improving Panda and started posting updates on Reddit.
Some posts went viral, repo hit 500⭐.

That's when I ran out of API credits, and Bennie Seybold noticed the project.
He helped me with Anthropic credits, which honestly kept Panda alive during that phase.

Around the same time, a French user named Tom started DM'ing feedback and bug reports. He believed in the project so much that he even volunteered to manage 
Panda's Instagram and Facebook pages.
People like him made it impossible to stop working on it.

By v4, Panda was wishing birthdays in 20 sec and handling long-range tasks, like DM'ing your new LinkedIn connections.

Then one night, I got an email from HowToMen.
He wanted to feature Panda in his video.
The video dropped, and Panda crashed within 15 min 😭
2K new users hit at once, and my infra collapsed.

People paid for Pro, got nothing.
Comments called it a scam.
Play Store reviews tanked.
I felt gutted.

Then my MDG Space, IITR alum group stepped in.
Kaustubh Trivedi revived the app.

When it crashed again, Madhur Rao jumped in.

Panda wouldn't exist without them.

Fun bit: I told Madhur it costs 6 K/day to run Panda.
He thought $6 K, and still agreed 😅
(It was ₹6 K.)

Just a small open-source side project I hacked together after hours, but it taught me something huge:
You can build the product yourself,
but it takes people to keep it alive. 💙

Also thanks to my brother, Ansh Chaudhary for being my unpaid intern, who sent me Briyani payment requests instead of invoices.

Also thanks to Harshita Singh, Usman Khatri, Dhanush Sugganahalli, @jules, @cursor, @kiro for contributing !!

PS. Thank you reading this far, if you know someone with Visual Impairment, please reach out!`
    },
    'accessibility-focus': {
      title: 'Beyond Screen Readers: Our Vision for True AI Accessibility',
      date: 'October 2025',
      category: 'Mission',
      readTime: '4 min read',
      imageUrl: '/nab-delhi.png',
      content: `I recently had the privilege of meeting with the people over at NAB Delhi (National Association for the Blind). These are people who live and breathe digital accessibility every day, so their feedback is invaluable.

It was a deeply encouraging session. The team's commendation for the initiative helped crystallize exactly why our approach is so different.

For years, digital accessibility has been defined by passive tools. Screen readers like TalkBack are life-changing, but they still place the full burden of navigation on the user. As the NAB team knows all too well, you have to learn complex gestures, remember app layouts, and listen to every single UI element just to get something done.

Panda is built on a different philosophy entirely. It’s designed to be an active agent instead of a passive reader.

What does this mean in practice? It means shifting the work from the user to the AI.

Instead of a user having to tap, tap, swipe through an app, they can simply state their goal: "Panda, book me an Uber from home to Connaught Place," or "Find the latest email from my bank and read it to me."

Panda sees the screen and operates the app to achieve that goal. It performs the taps, swipes, and text entries a sighted person would.

This is the fundamental difference the seasoned team at NAB Delhi recognized. It’s about understanding user intent and taking action.

This is our vision for the future of accessibility. A future where technology is a true assistant that handles the task, not just another interface to be learned. Having this vision validated by such dedicated experts is a powerful milestone for the entire project. I'm incredibly grateful for their time and insightful feedback.`
    }
  };

  const post = blogPosts[id];

  if (!post) {
    return (
      <div className="blog-page">
        <div className="container">
          <div className="not-found">
            <h1>Blog post not found</h1>
            <Link to="/blog" className="back-home-btn">← Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  const paragraphs = post.content.split('\n\n').filter(p => p.trim());

  return (
    <div className="blog-page">
      {/* Blog Header */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            className="blog-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="back-link-wrapper">
              <Link to="/blog" className="back-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
                Back to Blog
              </Link>
            </div>
            <h1 className="blog-title">
              {post.title}
            </h1>
            <div className="blog-meta-simple">
              <span>{post.date}</span>
              <span>&bull;</span>
              <span>{post.category}</span>
              <span>&bull;</span>
              <span>{post.readTime}</span>
            </div>

            {/* --- NEW FEATURED IMAGE --- */}
            <motion.img
              src={post.imageUrl}
              alt={post.title}
              className="blog-feature-image"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          </motion.div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="blog-content">
        <div className="container">
          <div className="blog-article">
            {paragraphs.map((paragraph, index) => (
              <motion.div
                key={index}
                className="blog-paragraph"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                {paragraph.includes('🚀') ? (
                  <div className="highlight-paragraph">
                    <span className="emoji">🚀</span>
                    <span className="text">{paragraph.replace('🚀', '').trim()}</span>
                  </div>
                ) : paragraph.includes('💙') ? (
                  <div className="highlight-paragraph heart">
                    <span className="text">{paragraph}</span>
                  </div>
                ) : paragraph.includes('PS.') ? (
                  <div className="ps-note">
                    <span className="text">{paragraph}</span>
                  </div>
                ) : (
                  <p className="regular-paragraph">{paragraph}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      <Footer />

      <style jsx>{`
        .blog-page {
          min-height: 100vh;
          /* The single, fixed, seamless background */
          background: 
            radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.05) 0%, transparent 50%),
            var(--gradient-dark);
          background-attachment: fixed;
          color: var(--text-primary);
        }

        .back-link-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          border-radius: 100px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }

        .back-link:hover {
          color: white;
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.25);
          transform: translateX(-4px);
        }

        .back-link svg {
          transition: transform 0.3s ease;
        }

        .back-link:hover svg {
          transform: translateX(-3px);
        }

        .blog-hero {
          padding: 120px 0 80px;
          position: relative;
          /* Division line */
          border-bottom: 1px solid rgba(255, 255, 255, 0.2); 
        }

        .blog-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .blog-meta-simple {
          display: flex;
          justify-content: center;
          gap: 12px;
          font-size: 1rem;
          color: var(--text-secondary);
          margin-top: 24px;
        }
        
        .blog-feature-image {
          width: 100%;
          height: auto;
          max-height: 450px;
          object-fit: cover;
          border-radius: 16px;
          margin-top: 48px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .blog-title {
          font-size: 3.5rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .blog-content {
          padding: 80px 0;
          /* No background, seamless */
        }

        .blog-article {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .blog-paragraph {
          margin-bottom: 32px;
        }

        .regular-paragraph {
          font-size: 1.125rem;
          line-height: 1.9; /* Increased for readability */
          color: var(--text-primary);
          margin: 0;
          opacity: 0.9;
        }

        /* --- UPDATED "GLASS" STYLES --- */
        .highlight-paragraph,
        .ps-note {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 24px;
          backdrop-filter: blur(10px);
        }

        .highlight-paragraph {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }
        
        .highlight-paragraph.heart {
          /* Keep the heart color override */
          background: rgba(6, 182, 212, 0.1);
          border-color: rgba(6, 182, 212, 0.3);
        }

        .highlight-paragraph .emoji {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .highlight-paragraph .text,
        .ps-note .text {
          font-size: 1.125rem;
          line-height: 1.8;
          color: var(--text-primary);
          margin: 0;
        }

        .ps-note {
          font-style: italic;
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.3);
        }
        /* --- END OF UPDATED STYLES --- */

        .not-found {
          text-align: center;
          padding: 120px 0;
        }

        .not-found h1 {
          font-size: 2.5rem;
          margin-bottom: 24px;
        }

        @media (max-width: 768px) {
          .blog-hero {
            padding: 80px 0 60px;
          }

          .blog-title {
            font-size: 2.5rem;
          }
          
          .blog-feature-image {
            margin-top: 32px;
          }

          .blog-meta-simple {
            font-size: 0.9rem;
            flex-wrap: wrap;
            gap: 8px;
          }

          .blog-content {
            padding: 60px 0;
          }

          .blog-article {
            padding: 0 16px;
          }

          .regular-paragraph,
          .highlight-paragraph .text,
          .ps-note .text {
            font-size: 1rem;
          }

          .highlight-paragraph, .ps-note {
            padding: 20px;
          }
        }
      `}</style>
    </div >
  );
};

export default BlogPost;